/**
 * PflSchG & PflSchSachkV Legal References
 *
 * Key paragraphs from German Plant Protection Act and
 * Plant Protection Expertise Ordinance.
 */

import type { LegalReference } from '@hofsync/design-system';

/**
 * PflSchG § 9 - Sachkundenachweis
 */
export const PFLSCHG_9: LegalReference = {
  id: 'pflschg-9',
  law: 'PflSchG',
  lawFullName: 'Pflanzenschutzgesetz',
  paragraph: '§ 9',
  title: 'Anwendung von Pflanzenschutzmitteln',
  shortCitation: 'PflSchG § 9',
  quote:
    '(1) Pflanzenschutzmittel dürfen nur von Personen angewandt werden, die über einen ' +
    'Sachkundenachweis verfügen. Der Sachkundenachweis wird erteilt, wenn die ' +
    'erforderlichen fachlichen Kenntnisse und Fertigkeiten nachgewiesen werden.',
  effectiveDate: '14. Februar 2012',
  lastAmended: '10. August 2021',
  sourceUrl: 'https://www.gesetze-im-internet.de/pflschg_2012/__9.html',
  relevanceExplanation:
    'Alle Personen, die Pflanzenschutzmittel anwenden, benötigen einen gültigen Sachkundenachweis.',
};

/**
 * PflSchG § 11 - Pflanzenschutzgeräte
 */
export const PFLSCHG_11: LegalReference = {
  id: 'pflschg-11',
  law: 'PflSchG',
  lawFullName: 'Pflanzenschutzgesetz',
  paragraph: '§ 11',
  title: 'Pflanzenschutzgeräte',
  shortCitation: 'PflSchG § 11',
  quote:
    '(1) Pflanzenschutzgeräte dürfen nur in Verkehr gebracht oder verwendet werden, ' +
    'wenn sie so beschaffen sind, dass ihre bestimmungsgemäße und sachgerechte Verwendung ' +
    'keine schädlichen Auswirkungen auf die Gesundheit von Mensch und Tier oder auf das ' +
    'Grundwasser oder sonstige erhebliche schädliche Auswirkungen, insbesondere auf den ' +
    'Naturhaushalt, hat.',
  effectiveDate: '14. Februar 2012',
  lastAmended: '10. August 2021',
  sourceUrl: 'https://www.gesetze-im-internet.de/pflschg_2012/__11.html',
  relevanceExplanation:
    'Pflanzenschutzgeräte müssen regelmäßig geprüft werden (alle 3 Jahre).',
};

/**
 * PflSchSachkV § 1 - Sachkundenachweis
 */
export const PFLSCHSACHKV_1: LegalReference = {
  id: 'pflschsachkv-1',
  law: 'PflSchSachkV',
  lawFullName: 'Pflanzenschutz-Sachkundeverordnung',
  paragraph: '§ 1',
  title: 'Sachkundenachweis',
  shortCitation: 'PflSchSachkV § 1',
  quote:
    '(1) Die zuständige Behörde stellt auf Antrag einen Sachkundenachweis aus, wenn die ' +
    'antragstellende Person die erforderliche Zuverlässigkeit und Sachkunde nach § 9 ' +
    'Absatz 1 des Pflanzenschutzgesetzes besitzt. ' +
    '(2) Die Sachkunde nach Absatz 1 besitzt, wer ' +
    '1. eine Prüfung nach den §§ 2 bis 4 erfolgreich abgelegt hat, oder ' +
    '2. einen Berufsabschluss oder ein Studium nach Anlage 1 nachweist.',
  effectiveDate: '6. Juli 2013',
  lastAmended: '1. Januar 2020',
  sourceUrl: 'https://www.gesetze-im-internet.de/pflschsachkv/__1.html',
  relevanceExplanation:
    'Der Sachkundenachweis ist die Grundvoraussetzung für die Anwendung von Pflanzenschutzmitteln.',
};

/**
 * PflSchSachkV § 7 - Fortbildung
 */
export const PFLSCHSACHKV_7: LegalReference = {
  id: 'pflschsachkv-7',
  law: 'PflSchSachkV',
  lawFullName: 'Pflanzenschutz-Sachkundeverordnung',
  paragraph: '§ 7',
  title: 'Fortbildung',
  shortCitation: 'PflSchSachkV § 7',
  quote:
    '(1) Personen, die im Besitz eines Sachkundenachweises nach § 1 sind, haben jeweils ' +
    'innerhalb eines Zeitraums von drei Jahren ab Ausstellung oder letzter Fortbildung ' +
    'an einer von der zuständigen Behörde anerkannten Fort- oder Weiterbildungsmaßnahme ' +
    'teilzunehmen. Die Teilnahme ist nachzuweisen.',
  effectiveDate: '6. Juli 2013',
  lastAmended: '1. Januar 2020',
  sourceUrl: 'https://www.gesetze-im-internet.de/pflschsachkv/__7.html',
  relevanceExplanation:
    'Der Sachkundenachweis muss alle 3 Jahre durch Fortbildung erneuert werden.',
};

/**
 * All Plant Protection legal references indexed by ID
 */
export const PFLANZENSCHUTZ_LEGAL_REFS: Record<string, LegalReference> = {
  'pflschg-9': PFLSCHG_9,
  'pflschg-11': PFLSCHG_11,
  'pflschsachkv-1': PFLSCHSACHKV_1,
  'pflschsachkv-7': PFLSCHSACHKV_7,
};
